import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
} from '@mui/material';
import {
  ContentCut as SeparatorIcon,
  Category as CategorizerIcon,
  Speed as DifficultyIcon,
  Psychology as AnswerIcon,
  TextFormat as LatexIcon,
  Assignment as SolutionIcon,
} from '@mui/icons-material';
import AnimatedProgress from './AnimatedProgress';

const agentConfig = [
  {
    key: 'separator',
    label: 'Separator',
    icon: SeparatorIcon,
    color: 'primary',
    description: 'Extracts questions from documents',
  },
  {
    key: 'categorizer',
    label: 'Categorizer',
    icon: CategorizerIcon,
    color: 'secondary',
    description: 'Assigns topics to questions',
  },
  {
    key: 'difficulty_rater',
    label: 'Difficulty Rater',
    icon: DifficultyIcon,
    color: 'info',
    description: 'Rates question difficulty',
  },
  {
    key: 'answer_generator',
    label: 'Answer Generator',
    icon: AnswerIcon,
    color: 'warning',
    description: 'Generates AI answers',
  },
  {
    key: 'latex_formatter',
    label: 'LaTeX Formatter',
    icon: LatexIcon,
    color: 'success',
    description: 'Formats questions in LaTeX',
  },
  {
    key: 'solution_generator',
    label: 'Solution Generator',
    icon: SolutionIcon,
    color: 'error',
    description: 'Generates worked solutions',
  },
];

export default function ProcessingMetrics({ metrics }) {
  if (!metrics) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">Processing Metrics</Typography>
          <Typography color="text.secondary">No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          AI Agent Processing Status
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Progress through each processing stage
        </Typography>

        <Box sx={{ mt: 3 }}>
          {agentConfig.map((agent) => {
            const agentMetrics = metrics[agent.key];
            const Icon = agent.icon;
            const percentage = agentMetrics?.percentage || 0;
            const processed = agentMetrics?.processed || 0;
            const total = metrics.total_segments || 0;

            return (
              <Box key={agent.key} sx={{ mb: 3 }}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Icon 
                      sx={{ 
                        fontSize: 30, 
                        color: `${agent.color}.main`,
                        opacity: 0.7 
                      }} 
                    />
                  </Grid>
                  <Grid item xs>
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2">{agent.label}</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            {processed} / {total}
                          </Typography>
                          <Chip 
                            label={`${percentage}%`} 
                            size="small" 
                            color={percentage === 100 ? 'success' : 'default'}
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {agent.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <AnimatedProgress
                          value={percentage}
                          height={6}
                          color={agent.color}
                          animate={true}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>

        {/* Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Total Segments:</strong> {metrics.total_segments || 0}
          </Typography>
          {metrics.latex_formatter?.percentage < 100 && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {metrics.total_segments - (metrics.latex_formatter?.processed || 0)} segments need processing
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}